import Breadcrumb from "@/components/Breadcrumb"
import LimitPaginationComponent from "@/components/Table/LimitPaginationComponent"
import SearchComponent from "@/components/Table/SearchComponent"
import TableComponent from "@/components/Table/TabelComponent"
import { DataStatusRental, IDropdown, LIMIT_PAGINATE } from "@/constants/default"
import { dataQueryApi, dataQueryClient } from "@/utils/query"
import queryString from "query-string"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { createSearchParams, useLocation, useNavigate } from "react-router-dom"
import TableHeader from "./_header"
import _ from "lodash"
import { usePermissions } from "@/rbac"
import { permissions } from "@/rbac/permissions"
import { useGetHouseRentalExpiredQuery, useGetHouseRentalsQuery } from "@/services/house-rental.service"
import TableExpiredHeader from "./_header-expired"
import DropdownCheckbox from "@/components/Dropdown/DropdownCheckbox"

const defaultSort: string = 'ta_end'
const defaultOrder: string = "desc"

export default function Rentals() {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const parsed = queryString.parse(location?.search)
    const DataStatus = DataStatusRental()
    const limit = _.isString(parsed.l) ? parseInt(parsed?.l) : LIMIT_PAGINATE
    const {data: houseRentals, refetch, isFetching, isLoading} = useGetHouseRentalsQuery(dataQueryApi(location?.search, {limit, field: 'ta_end', status: Object.hasOwn(parsed, 'status') ? parsed.status : "2,3"}), {refetchOnMountOrArgChange: true})
    const {hasPermission} = usePermissions()
    const {data: houseRentalExpired, refetch: refetchLandlordExpired, isFetching: isFetchingLandlordExpired, isLoading: isLoadingLandlordExpired} = useGetHouseRentalExpiredQuery({}, {refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true})

    const getData = (type: string, result: any) => {
        let data = dataQueryClient(location.search, {field: 'ta_end'})
        switch (type) {
            case "search":
                data.s = result
                data.p = "1"
                break
            case "sort":
                data.o = result[0].desc ? "desc" : "asc"
                data.f = result[0].id
                break
            case "page":
                data.p = result
                break
            case "limit":
                data.l = result
                data.p = "1"
                break;
            case "status":
                data.status = result.map((s: IDropdown) => s.value).join(",")
                break
            default:
                data = { ...data };
                break;
        }
        navigate({
            search: createSearchParams(data).toString()
        })
    }

    useEffect(() => {
        if(location.state?.reload) {
            if (houseRentals?.data?.length === 1 && Number(parsed.p) !== 1) {
                getData("page", 1)
            } else {
                refetch()
                refetchLandlordExpired()
            }
        }
    }, [location.state?.reload])

    const renderTotalLandords = (total: number) => {
        let landlord = "landlord"
        if (total === 0) {
            return ""
        } else if (total > 1) {
            landlord = "landlords"
        }
        return ` ( ${total} ${landlord} )`
    }

    return (
        <>
            <Breadcrumb pageName={t("house_rental.house_rentals")} />
            <div className="flex flex-col gap-10">
                <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-10">
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-danger flex items-center ps-2 font-semibold dark:text-white">{`${t("house_rental.expired_month")}${renderTotalLandords(houseRentalExpired?.month?.length || 0)}`}</p>
                            <TableComponent
                                data={houseRentalExpired?.month || []}
                                columns={TableExpiredHeader()}
                                isPagination={false}
                                isLoading={isFetchingLandlordExpired || isLoadingLandlordExpired}
                            />
                        </div>
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-warning flex items-center ps-2 font-semibold dark:text-white">{`${t("house_rental.expired_2months")}${renderTotalLandords(houseRentalExpired?.two_months?.length || 0)}`}</p>
                            <TableComponent
                                data={houseRentalExpired?.two_months || []}
                                columns={TableExpiredHeader()}
                                isPagination={false}
                                isLoading={isFetchingLandlordExpired || isLoadingLandlordExpired}
                            />
                        </div>
                        <div className="max-w-full overflow-x-auto mt-3">
                            <p className="h-9 bg-yellow-300 flex items-center ps-2 font-semibold dark:text-white">{`${t("house_rental.expired_3months")}${renderTotalLandords(houseRentalExpired?.three_months?.length || 0)}`}</p>
                            <TableComponent
                                data={houseRentalExpired?.three_months || []}
                                columns={TableExpiredHeader()}
                                isPagination={false}
                                isLoading={isFetchingLandlordExpired || isLoadingLandlordExpired}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                        <div className="flex flex-col md:flex-row col-span-2">
                            <LimitPaginationComponent limit={limit} onChange={(s: number) => getData("limit", s)} />
                            <SearchComponent search={parsed?.s?.toString() || ""} onChange={(s) => getData("search", s)} />
                            <div className="ms-4">
                                <DropdownCheckbox data={DataStatus} onFilter={(selected) => getData("status", selected)} selected={DataStatus.filter((s) => (Object.hasOwn(parsed, 'status') ? parsed.status : "2,3")?.toString().split(",").includes(s.value))}/>
                            </div>
                        </div>
                        <div className="flex justify-start md:justify-end mt-2 md:mt-0">
                            <div className="me-2">
                                <button className="flex w-full justify-center items-center rounded bg-primary py-1.5 px-2 font-medium text-white text" onClick={() => {
                                    refetch()
                                    refetchLandlordExpired()
                                }}>
                                    <i className="fa-sharp fa-light fa-arrows-rotate me-2 text-white" />{t("table.reload")}
                                </button>
                            </div>
                            {
                                hasPermission(permissions.RENTAL.ADD) && <div>
                                    <button className="flex w-full justify-center items-center rounded bg-primary py-1.5 px-2 font-medium text-white text" onClick={() => navigate(location, { state: { modal: "/house-rentals/add" } })}><i className="fa-light fa-plus me-2 text-white" />{t("table.add")}</button>
                                </div>
                            }
                        </div>
                    </div>

                    <div className="max-w-full overflow-x-auto mt-3">
                        <TableComponent
                            data={houseRentals?.data || []}
                            columns={TableHeader()}
                            pageSize={limit}
                            isPagination={true}
                            currentPage={_.isString(parsed?.p) ? parseInt(parsed?.p) - 1 : 0}
                            totalPages={Math.ceil((houseRentals?.total_records || 0) / limit)}
                            onChangePage={(page: number) => getData("page", page + 1)}
                            isLoading={isFetching || isLoading}
                            sort={[
                                {
                                    id: parsed?.f?.toString() || defaultSort,
                                    desc: parsed?.o === defaultOrder ? true : false || true
                                }
                            ]}
                            onChangeSort={data => getData("sort", data)}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}
